/*
 * Product Bundle template
 * Like Product Upsell, but the toggled items come from a collection and the
 * summary shows an auto-calculated bundle saving (default 15%) once 1+ extra
 * item is added. The saving is indicative — the real discount is applied at
 * checkout by a Shopify automatic discount. Every line carries a _bundle_id so
 * the backend discount can target the set. No external libraries.
 */
if (!customElements.get('product-bundle')) {
  class ProductBundle extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-pu-data]');
      if (!dataEl) return;
      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[product-bundle] Could not parse bundle data:', e); // eslint-disable-line
        return;
      }
      if (!this.config.variants || !this.config.variants.length) return;
      this.config.addons = this.config.addons || [];
      this.bundleRate = Number(this.config.bundleDiscount) || 0;
      this.bundleUnlock = Number(this.config.bundleUnlock) || 1;

      this.cartDrawer = document.querySelector('cart-drawer');
      this.moneyFormat = this.config.moneyFormat || '£{{amount}}';

      const first = this.config.variants.find((v) => v.available) || this.config.variants[0];
      this.state = {
        options: first.options.slice(),
        qty: 1,
        addons: {}
      };

      this.cacheDom();
      this.bindEvents();
      this.render();
    }

    cacheDom() {
      this.optionEls = Array.from(this.querySelectorAll('[data-pu-opt]'));
      this.galleryStage = this.querySelector('[data-pu-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-pu-thumb]'));
      this.priceEl = this.querySelector('[data-pu-price]');
      this.qtyVal = this.querySelector('[data-pu-qty-val]');
      this.qtyInc = this.querySelector('[data-pu-qty-inc]');
      this.qtyDec = this.querySelector('[data-pu-qty-dec]');
      this.addonEls = Array.from(this.querySelectorAll('[data-pu-addon]'));
      this.totalEl = this.querySelector('[data-pu-total]');
      this.savingEl = this.querySelector('[data-pu-saving]');
      this.bundleCountEl = this.querySelector('[data-pu-bundle-count]');
      this.payEl = this.querySelector('[data-pu-pay]');
      this.atc = this.querySelector('[data-pu-atc]');
      this.errorEl = this.querySelector('[data-pu-error]');
      this.summary = this.querySelector('[data-pu-summary]');
    }

    bindEvents() {
      this.optionEls.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.options[Number(btn.dataset.puOptIndex)] = btn.dataset.puOptValue;
          this.render();
        });
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setHero(thumb.dataset.puThumb, thumb));
      });

      if (this.qtyInc) this.qtyInc.addEventListener('click', () => this.changeQty(1));
      if (this.qtyDec) this.qtyDec.addEventListener('click', () => this.changeQty(-1));

      // Whole card toggles; the Quick View button opens a popup instead.
      this.addonEls.forEach((card) => {
        if (card.classList.contains('is-unavailable')) return;
        card.addEventListener('click', (e) => {
          if (e.target.closest('[data-pu-quickview]')) { this.openQuickView(card); return; }
          if (e.target.closest('a')) return;
          this.toggleAddon(card.dataset.puAddon, card);
        });
        const qv = card.querySelector('[data-pu-quickview]');
        if (qv) qv.setAttribute('aria-haspopup', 'dialog');
      });

      if (this.atc) this.atc.addEventListener('click', () => this.addToCart());

      // Sync with the theme's native variant picker (if added as a block).
      document.addEventListener('on:variant:change', (e) => {
        if (!e.detail) return;
        this.themeVariantId = e.detail.variant ? e.detail.variant.id : 'none';
        this.render();
      });
    }

    get variant() {
      if (this.themeVariantId !== undefined) {
        if (this.themeVariantId === 'none') return undefined;
        return this.config.variants.find((v) => String(v.id) === String(this.themeVariantId));
      }
      return this.config.variants.find((v) =>
        v.options.every((opt, i) => opt === this.state.options[i])
      );
    }

    changeQty(delta) {
      this.state.qty = Math.max(1, this.state.qty + delta);
      this.render();
    }

    toggleAddon(key, card) {
      const addon = this.config.addons.find((a) => a.key === key);
      if (!addon || !addon.available) return;
      this.state.addons[key] = !this.state.addons[key];
      card.classList.toggle('is-selected', this.state.addons[key]);
      const toggle = card.querySelector('[data-pu-toggle]');
      if (toggle) {
        toggle.textContent = this.state.addons[key] ? 'Added' : 'Add';
        toggle.setAttribute('aria-pressed', String(this.state.addons[key]));
      }
      if (this.state.addons[key]) {
        this.track('bundle_item_added', { item: addon.title });
      }
      if (this.qvModal && !this.qvModal.hidden && this.qvKey === key) this.syncQvAdd();
      this.renderSummary();
    }

    /* ---------- Quick View ---------- */
    ensureModal() {
      if (this.qvModal) return this.qvModal;
      const modal = document.createElement('div');
      modal.className = 'pu-qv';
      modal.hidden = true;
      modal.innerHTML =
        '<div class="pu-qv__backdrop" data-pu-qv-close></div>' +
        '<div class="pu-qv__dialog" role="dialog" aria-modal="true" aria-label="Product quick view">' +
        '<button class="pu-qv__close" data-pu-qv-close type="button" aria-label="Close">&times;</button>' +
        '<div class="pu-qv__content" data-pu-qv-content></div></div>';
      this.appendChild(modal);
      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-pu-qv-close]')) { this.closeQuickView(); return; }
        const thumb = e.target.closest('[data-pu-qv-thumb]');
        if (thumb) {
          const stage = this.qvContent.querySelector('[data-pu-qv-stage]');
          if (stage) stage.src = thumb.getAttribute('data-pu-qv-thumb');
          this.qvContent.querySelectorAll('[data-pu-qv-thumb]').forEach((t) => {
            t.setAttribute('aria-selected', String(t === thumb));
          });
          return;
        }
        if (e.target.closest('[data-pu-qv-add]')) { this.toggleByKey(this.qvKey); }
      });
      this.qvKeydown = (e) => { if (e.key === 'Escape') this.closeQuickView(); };
      this.qvModal = modal;
      this.qvContent = modal.querySelector('[data-pu-qv-content]');
      return modal;
    }

    openQuickView(card) {
      const tpl = card.querySelector('[data-pu-qv]');
      if (!tpl || !('content' in tpl)) return;
      this.ensureModal();
      this.qvKey = card.dataset.puAddon;
      this.qvContent.innerHTML = '';
      this.qvContent.appendChild(tpl.content.cloneNode(true));
      this.syncQvAdd();
      this.qvModal.hidden = false;
      document.body.classList.add('pu-qv-open');
      document.addEventListener('keydown', this.qvKeydown);
      this.qvReturnFocus = card.querySelector('[data-pu-quickview]') || card;
      const closeBtn = this.qvModal.querySelector('[data-pu-qv-close]');
      if (closeBtn) closeBtn.focus();
    }

    closeQuickView() {
      if (!this.qvModal || this.qvModal.hidden) return;
      this.qvModal.hidden = true;
      document.body.classList.remove('pu-qv-open');
      document.removeEventListener('keydown', this.qvKeydown);
      if (this.qvReturnFocus && this.qvReturnFocus.focus) this.qvReturnFocus.focus();
    }

    toggleByKey(key) {
      const card = this.addonEls.find((c) => c.dataset.puAddon === key);
      if (card) this.toggleAddon(key, card);
    }

    syncQvAdd() {
      const btn = this.qvContent && this.qvContent.querySelector('[data-pu-qv-add]');
      if (!btn) return;
      const on = !!this.state.addons[this.qvKey];
      btn.textContent = on ? 'Added to bundle ✓' : 'Add to bundle';
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', String(on));
    }

    setHero(src, thumb) {
      if (!this.galleryStage || !src) return;
      const img = this.galleryStage.querySelector('img');
      if (img) img.src = src;
      this.thumbs.forEach((t) => t.setAttribute('aria-selected', String(t === thumb)));
    }

    render() {
      const variant = this.variant;

      this.optionEls.forEach((btn) => {
        const idx = Number(btn.dataset.puOptIndex);
        const val = btn.dataset.puOptValue;
        btn.setAttribute('aria-pressed', String(this.state.options[idx] === val));
        const possible = this.config.variants.some((v) =>
          v.available && v.options[idx] === val &&
          v.options.every((o, i) => i === idx || o === this.state.options[i])
        );
        btn.classList.toggle('is-unavailable', !possible);
      });

      this.querySelectorAll('[data-pu-opt-name]').forEach((el) => {
        el.textContent = this.state.options[Number(el.dataset.puOptName)];
      });

      if (variant && variant.image && this.galleryStage) {
        const img = this.galleryStage.querySelector('img');
        if (img) img.src = variant.image;
      }

      if (this.qtyVal) this.qtyVal.textContent = this.state.qty;

      if (this.priceEl && variant) {
        const onSale = variant.compareAt && variant.compareAt > variant.price;
        let html = this.money(variant.price);
        if (onSale) {
          html += `<del>${this.money(variant.compareAt)}</del>`;
          const savePct = Math.round((1 - variant.price / variant.compareAt) * 100);
          if (savePct > 0) html += `<span class="pu-price__save">Save ${savePct}%</span>`;
        }
        this.priceEl.classList.toggle('pu-price--sale', Boolean(onSale));
        this.priceEl.innerHTML = html;
      }

      this.renderSummary();
    }

    renderSummary() {
      const variant = this.variant;
      const available = variant && variant.available;
      let subtotal = 0;
      let addedCount = 0;

      if (variant) subtotal += variant.price * this.state.qty;

      this.config.addons.forEach((addon) => {
        if (this.state.addons[addon.key] && addon.available) {
          subtotal += addon.price;
          addedCount += 1;
        }
      });

      // Bundle saving: whole bundle, unlocked once enough extras are added.
      let saving = 0;
      if (addedCount >= this.bundleUnlock && this.bundleRate > 0) {
        saving = Math.round(subtotal * this.bundleRate);
      }
      const finalTotal = subtotal - saving;

      if (this.totalEl) this.totalEl.textContent = this.money(finalTotal);

      if (this.bundleCountEl) {
        this.bundleCountEl.textContent = addedCount;
        this.bundleCountEl.hidden = addedCount === 0;
      }

      if (this.savingEl) {
        if (saving > 0) {
          const pct = Math.round(this.bundleRate * 100);
          const label = this.config.savingLabel || 'Bundle saving';
          this.savingEl.innerHTML =
            `${label} (${pct}%) <span class="pu-saving__was">${this.money(subtotal)}</span>` +
            `<b class="pu-saving__amount">−${this.money(saving)}</b>`;
          this.savingEl.hidden = false;
        } else {
          this.savingEl.hidden = true;
          this.savingEl.textContent = '';
        }
      }

      if (this.payEl) {
        const four = (finalTotal / 4 / 100).toFixed(2);
        this.payEl.innerHTML = `Clearpay: 4 payments of £${four}`;
      }

      this.total = finalTotal;

      if (this.atc) {
        this.atc.classList.toggle('is-soldout', !available);
        this.atc.disabled = !available;
        this.atc.textContent = available ? this.config.atcLabel : this.config.soldOutLabel;
      }
    }

    /* ---------- Cart ---------- */
    buildItems() {
      const variant = this.variant;
      const buildId = `bn-${Math.floor(Date.now() / 1000)}`;
      const items = [{
        id: variant.id,
        quantity: this.state.qty,
        properties: this.cleanProps({ _bundle_id: buildId, Delivery: this.config.delivery })
      }];

      this.config.addons.forEach((addon) => {
        if (this.state.addons[addon.key] && addon.available) {
          items.push({
            id: addon.variantId,
            quantity: 1,
            properties: this.cleanProps({ _bundle_id: buildId, 'Bundled with': this.config.productTitle })
          });
        }
      });
      return items;
    }

    cleanProps(obj) {
      const out = {};
      Object.keys(obj).forEach((k) => { if (obj[k]) out[k] = obj[k]; });
      return out;
    }

    async addToCart() {
      const variant = this.variant;
      if (!variant || !variant.available || this.atc.classList.contains('is-loading')) return;
      this.setError(null);
      this.atc.classList.add('is-loading');
      this.atc.disabled = true;

      const items = this.buildItems();

      try {
        const res = await fetch(`${window.theme.routes.cartAdd}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items,
            sections: 'cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

        this.track('bundle_add_to_cart', {
          value: this.total / 100,
          items: items.map((i) => ({ id: i.id, quantity: i.quantity }))
        });
        this.fireGa4AddToCart(items);

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (window.theme.settings.afterAtc === 'page') {
          window.location.href = window.theme.routes.cart;
          return;
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        if (this.cartDrawer) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            detail: { opener: this.atc }
          }));
        }

        this.atc.classList.add('is-success');
      } catch (err) {
        this.setError(err.message);
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.disabled = false;
        setTimeout(() => this.atc.classList.remove('is-success'), 1400);
      }
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      if (msg) this.errorEl.textContent = msg;
    }

    /* ---------- Analytics ---------- */
    track(event, payload) {
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({ event, ...payload });
      }
      this.dispatchEvent(new CustomEvent(`bundle:${event}`, { bubbles: true, detail: payload }));
    }

    fireGa4AddToCart(items) {
      if (typeof window.gtag !== 'function') return;
      const names = [this.config.productTitle].concat(
        this.config.addons.filter((a) => this.state.addons[a.key]).map((a) => a.title)
      );
      window.gtag('event', 'add_to_cart', {
        currency: this.config.currency || 'GBP',
        value: this.total / 100,
        items: items.map((i, idx) => ({
          item_id: i.id,
          item_name: names[idx],
          quantity: i.quantity
        }))
      });
    }

    /* ---------- Money ---------- */
    money(cents) {
      const value = (cents / 100).toFixed(2);
      const [pounds, pence] = value.split('.');
      const withCommas = pounds.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${pence}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${pence}`);
    }
  }

  customElements.define('product-bundle', ProductBundle);
}
